// apps/client/src/pages/brokers/RecipientSplitControls.tsx
//
// Per-person arrangement controls, shared by the Broker Assistants and Credit
// Analysts cards. The two lists exist because tenants use different words for
// the same idea; the controls underneath are identical, so they live here once.
//
// Every control maps to a field `updateBrokerCommissionConfig` persists and
// `computeRecipientPayout` honours. A control that had no backing field would
// be a lie, so there are none.

import React from 'react';

export interface RecipientRateSet {
  upfrontSharePct?: number;
  trailSharePct?: number;
}

export interface RecipientOptions {
  base?: 'broker_share' | 'gross';
  reducesBrokerShare?: boolean;
  appliesTo?: 'every_loan' | 'assigned_only';
  byIntroduction?: { broker?: RecipientRateSet; company?: RecipientRateSet };
}

interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; tone?: 'blue' | 'emerald' | 'amber' }>;
  onChange: (v: T) => void;
  describedBy?: string;
}

function Segmented<T extends string>({ label, value, options, onChange, describedBy }: SegmentedProps<T>) {
  const toneClass = (tone: 'blue' | 'emerald' | 'amber' | undefined, active: boolean) => {
    if (!active) return 'text-gray-400 hover:text-gray-200';
    switch (tone) {
      case 'emerald':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
      case 'amber':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      default:
        return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
    }
  };
  return (
    <div role="group" aria-label={label}>
      <p className="text-[0.625rem] uppercase tracking-wide font-semibold text-gray-400 mb-1.5">{label}</p>
      <div className="inline-flex rounded-lg border border-gray-700 bg-gray-900/60 p-0.5">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={active}
              aria-describedby={describedBy}
              onClick={() => onChange(o.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                active ? toneClass(o.tone, true) : `border-transparent ${toneClass(o.tone, false)}`
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const PctInput: React.FC<{
  label: string;
  ariaLabel: string;
  value: number;
  onChange: (n: number) => void;
}> = ({ label, ariaLabel, value, onChange }) => (
  <label className="flex items-center gap-1.5">
    <input
      type="number"
      min={0}
      max={100}
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => {
        const raw = parseInt(e.target.value, 10);
        onChange(isNaN(raw) ? 0 : Math.max(0, Math.min(100, raw)));
      }}
      className="w-16 bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-white text-center font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
    <span className="text-gray-400 text-sm font-semibold">%</span>
    <span className="text-[0.625rem] uppercase tracking-wide font-semibold text-gray-500">{label}</span>
  </label>
);

export interface RecipientSplitControlsProps {
  displayName: string;
  /** Headline rates, used when no per-introduction override is set. */
  upfrontPct: number;
  trailPct: number;
  onRatesChange: (next: { upfrontPct: number; trailPct: number }) => void;
  options: RecipientOptions;
  onOptionsChange: (next: RecipientOptions) => void;
  /** This recipient kind's default, so the UI shows the effective value even
   *  when the field is absent (absent is how defaults are persisted). */
  defaultAppliesTo: 'every_loan' | 'assigned_only';
  /** The broker's own share of gross, for the worked example only. */
  brokerSharePct: number;
  /** Wording differs: an assistant's "their loans" is what she originated. */
  assignedOnlyLabel: string;
}

const EXAMPLE_GROSS_CENTS = 100_000; // $1,000

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const RecipientSplitControls: React.FC<RecipientSplitControlsProps> = ({
  displayName,
  upfrontPct,
  trailPct,
  onRatesChange,
  options,
  onOptionsChange,
  defaultAppliesTo,
  brokerSharePct,
  assignedOnlyLabel,
}) => {
  const base = options.base ?? 'broker_share';
  const reduces = options.reducesBrokerShare !== false;
  const appliesTo = options.appliesTo ?? defaultAppliesTo;
  const perIntroduction = !!options.byIntroduction;

  const setIntroRate = (
    kind: 'broker' | 'company',
    leg: 'upfrontSharePct' | 'trailSharePct',
    v: number,
  ) => {
    onOptionsChange({
      ...options,
      byIntroduction: {
        ...options.byIntroduction,
        [kind]: { ...options.byIntroduction?.[kind], [leg]: v },
      },
    });
  };

  const togglePerIntroduction = (on: boolean) => {
    if (!on) {
      const { byIntroduction, ...rest } = options;
      onOptionsChange(rest);
      return;
    }
    // Seed both introduction types from the headline rates so switching mode
    // never silently changes what this person is paid.
    onOptionsChange({
      ...options,
      byIntroduction: {
        broker: { upfrontSharePct: upfrontPct, trailSharePct: trailPct },
        company: { upfrontSharePct: upfrontPct, trailSharePct: trailPct },
      },
    });
  };

  // Worked example. Mirrors computeRecipientPayout: the percentage applies to
  // gross or to the broker's share, and only comes out of the broker when the
  // arrangement says it does.
  const brokerShareCents = Math.round((EXAMPLE_GROSS_CENTS * brokerSharePct) / 100);
  const allocationBase = base === 'gross' ? EXAMPLE_GROSS_CENTS : brokerShareCents;
  const recipientCents = Math.round((allocationBase * upfrontPct) / 100);
  const brokerNetCents = reduces ? brokerShareCents - recipientCents : brokerShareCents;
  const businessCents = EXAMPLE_GROSS_CENTS - brokerNetCents - recipientCents;

  return (
    <div className="mt-3 space-y-4">
      <div>
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <Segmented
            label="Rate"
            value={perIntroduction ? 'diff' : 'same'}
            options={[
              { value: 'same', label: 'One rate for both' },
              { value: 'diff', label: 'Different by introduction' },
            ]}
            onChange={(v) => togglePerIntroduction(v === 'diff')}
          />
        </div>

        {!perIntroduction ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-gray-300 w-[11.5rem]">All introduction types</span>
            <PctInput
              label="upfront"
              ariaLabel={`${displayName} upfront share percent`}
              value={upfrontPct}
              onChange={(n) => onRatesChange({ upfrontPct: n, trailPct })}
            />
            <PctInput
              label="trail"
              ariaLabel={`${displayName} trail share percent`}
              value={trailPct}
              onChange={(n) => onRatesChange({ upfrontPct, trailPct: n })}
            />
          </div>
        ) : (
          <div className="space-y-2">
            {(['broker', 'company'] as const).map((kind) => (
              <div key={kind} className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-gray-300 w-[11.5rem]">
                  {kind === 'broker' ? 'Broker introduction' : 'Company introduction'}
                </span>
                <PctInput
                  label="upfront"
                  ariaLabel={`${displayName} ${kind} introduction upfront share percent`}
                  value={options.byIntroduction?.[kind]?.upfrontSharePct ?? upfrontPct}
                  onChange={(n) => setIntroRate(kind, 'upfrontSharePct', n)}
                />
                <PctInput
                  label="trail"
                  ariaLabel={`${displayName} ${kind} introduction trail share percent`}
                  value={options.byIntroduction?.[kind]?.trailSharePct ?? trailPct}
                  onChange={(n) => setIntroRate(kind, 'trailSharePct', n)}
                />
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Set a rate to <span className="text-gray-300 font-medium">0</span> if this person is not paid on it.
          Introduction type is tagged per loan in the Loan Tagger below.
        </p>
      </div>

      <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
        <Segmented
          label="Basis"
          value={base}
          options={[
            { value: 'broker_share', label: '% of broker share' },
            { value: 'gross', label: '% of gross' },
          ]}
          onChange={(v) => onOptionsChange({ ...options, base: v })}
        />
        <Segmented
          label="Paid by"
          value={reduces ? 'broker' : 'business'}
          options={[
            { value: 'broker', label: 'The broker' },
            { value: 'business', label: 'The business', tone: 'emerald' },
          ]}
          onChange={(v) => onOptionsChange({ ...options, reducesBrokerShare: v === 'broker' })}
        />
        <Segmented
          label="Applies to"
          value={appliesTo}
          options={[
            { value: 'every_loan', label: 'Every loan' },
            { value: 'assigned_only', label: assignedOnlyLabel, tone: 'amber' },
          ]}
          onChange={(v) => onOptionsChange({ ...options, appliesTo: v })}
        />
      </div>

      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-xs text-gray-300">
        <span className="font-semibold text-emerald-300">
          On a {money(EXAMPLE_GROSS_CENTS)} upfront commission
          {appliesTo === 'assigned_only' ? ' assigned to them' : ''}:
        </span>{' '}
        {displayName} earns <span className="font-semibold text-white">{money(recipientCents)}</span>{' '}
        ({upfrontPct}% of {base === 'gross' ? 'gross' : "the broker's share"}) · the broker keeps{' '}
        <span className="font-semibold text-white">{money(brokerNetCents)}</span>
        {reduces ? '' : ' (untouched — the business bears this)'} · the business keeps{' '}
        <span className="font-semibold text-white">{money(businessCents)}</span>.
        {appliesTo === 'assigned_only' && ' Loans not assigned to them pay nothing.'}
      </div>
    </div>
  );
};

export default RecipientSplitControls;
